import React, { ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CarouselRef } from 'antd-v5/es/carousel';
import { Flex, Image, Text, URL } from 'UI';
import {
  SImageBrowser,
  SImageCarousel,
  SImagePlaceholder,
  SImageSlide,
  SThumbnail,
  SThumbnailStrip,
} from './ReuseImageGallery.styled';

interface ReuseImageGalleryProps {
  images: string[];
  width: number | '100%';
  height: number;
  imageTestId: string;
  mainImageTestId: string;
  placeholderTestId: string;
  placeholder?: ReactNode;
  showThumbnails?: boolean;
  thumbnailTestId?: string;
  thumbnailStripTestId?: string;
  selectedIndex?: number;
  onSelectIndex?: (index: number) => void;
}

export const ReuseImageGallery: React.FC<ReuseImageGalleryProps> = ({
  images,
  width,
  height,
  imageTestId,
  mainImageTestId,
  placeholderTestId,
  placeholder,
  showThumbnails = false,
  thumbnailTestId,
  thumbnailStripTestId,
  selectedIndex: controlledSelectedIndex,
  onSelectIndex,
}) => {
  const { t } = useTranslation();
  const carouselRef = useRef<CarouselRef>(null);
  const [internalSelectedIndex, setInternalSelectedIndex] = useState(0);

  const selectedIndex = controlledSelectedIndex ?? internalSelectedIndex;

  const handleCarouselChange = (index: number) => {
    setInternalSelectedIndex(index);
    onSelectIndex?.(index);
  };

  const handleThumbnailClick = (index: number) => {
    carouselRef.current?.goTo(index);
    handleCarouselChange(index);
  };

  const defaultPlaceholder = (
    <SImagePlaceholder $width={width} $height={height} data-testid={placeholderTestId}>
      <Text type={'light_12_black_65'}>{t('catalog.reuse_availabilities.card.no_image', 'No image available')}</Text>
    </SImagePlaceholder>
  );

  const renderedPlaceholder = placeholder ?? defaultPlaceholder;

  const renderImage = (src: string, index: number) => (
    <SImageSlide key={`${src}-${index}`} $width={width} $height={height} data-testid={`${mainImageTestId}-${index}`}>
      <Image src={src} type={URL} alt={'reference_image'} fallbackComponent={renderedPlaceholder} />
    </SImageSlide>
  );

  if (images.length === 0) {
    return renderedPlaceholder;
  }

  return (
    <Flex direction={'column'} gap={12} minWidth={0}>
      <SImageBrowser $width={width} $height={height} data-testid={imageTestId}>
        {images.length === 1 ? (
          renderImage(images[0], 0)
        ) : (
          <SImageCarousel
            ref={carouselRef}
            arrows
            dots
            draggable
            infinite={false}
            afterChange={handleCarouselChange}
            $width={width}
            $height={height}
          >
            {images.map((src, index) => renderImage(src, index))}
          </SImageCarousel>
        )}
      </SImageBrowser>
      {showThumbnails && images.length > 1 && thumbnailTestId && thumbnailStripTestId && (
        <SThumbnailStrip data-testid={thumbnailStripTestId}>
          {images.map((src, index) => (
            <SThumbnail
              key={`${src}-${index}`}
              type={'button'}
              $selected={index === selectedIndex}
              data-testid={`${thumbnailTestId}-${index}`}
              onClick={() => handleThumbnailClick(index)}
            >
              <img src={src} alt={'reference_thumbnail'} />
            </SThumbnail>
          ))}
        </SThumbnailStrip>
      )}
    </Flex>
  );
};
